/**
 * Esquemas de validación para la configuración JSON del Sidenav
 */

// Esquema para validar elementos de navegación
export const navigationItemSchema = {
  type: 'object',
  required: ['id', 'label', 'icon'],
  properties: {
    id: { type: 'string', minLength: 1 },
    label: { type: 'string', minLength: 1 },
    icon: { type: 'string', minLength: 1 },
    href: { type: 'string' },
    onClick: { type: 'string' }, // Nombre de función
    children: {
      type: 'array',
      items: { $ref: '#/definitions/navigationItem' }
    },
    badge: {
      oneOf: [
        { type: 'string' },
        { type: 'number' }
      ]
    },
    isActive: { type: 'boolean' },
    isDisabled: { type: 'boolean' },
    tooltip: { type: 'string' },
    metadata: { type: 'object' }
  }
};

// Esquema para configuración del logo
export const logoSchema = {
  type: 'object',
  properties: {
    text: { type: 'string', minLength: 1 },
    subtext: { type: 'string' },
    icon: { type: 'string' },
    href: { type: 'string' },
    type: { 
      type: 'string', 
      enum: ['text', 'image', 'svg', 'component'],
      default: 'text'
    },
    src: { type: 'string' }, // URL para imágenes
    alt: { type: 'string' }, // Alt text para imágenes
    width: { type: 'number', minimum: 1 }, // Ancho personalizado
    height: { type: 'number', minimum: 1 }, // Alto personalizado
    component: { // Para componentes React personalizados
      type: 'object',
      properties: {
        name: { type: 'string' }, // Nombre del componente
        props: { type: 'object' } // Props del componente
      }
    },
    image: { // Mantener compatibilidad hacia atrás
      type: 'object',
      properties: {
        src: { type: 'string', minLength: 1 },
        alt: { type: 'string' },
        width: { type: 'number', minimum: 1 },
        height: { type: 'number', minimum: 1 }
      },
      required: ['src', 'alt']
    },
    size: { // Tamaño por defecto
      type: 'object',
      properties: {
        width: { type: 'number', minimum: 1, default: 32 },
        height: { type: 'number', minimum: 1, default: 32 }
      }
    },
    variants: {
      type: 'object',
      properties: {
        collapsed: {
          type: 'object',
          properties: {
            text: { type: 'string' },
            icon: { type: 'string' },
            type: { type: 'string', enum: ['text', 'image', 'svg', 'component'] },
            src: { type: 'string' },
            alt: { type: 'string' },
            width: { type: 'number', minimum: 1 },
            height: { type: 'number', minimum: 1 },
            size: {
              type: 'object',
              properties: {
                width: { type: 'number', minimum: 1 },
                height: { type: 'number', minimum: 1 }
              }
            },
            component: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                props: { type: 'object' }
              }
            }
          }
        },
        semiCollapsed: {
          type: 'object',
          properties: {
            text: { type: 'string' },
            icon: { type: 'string' },
            type: { type: 'string', enum: ['text', 'image', 'svg', 'component'] },
            src: { type: 'string' },
            alt: { type: 'string' },
            width: { type: 'number', minimum: 1 },
            height: { type: 'number', minimum: 1 },
            size: {
              type: 'object',
              properties: {
                width: { type: 'number', minimum: 1 },
                height: { type: 'number', minimum: 1 }
              }
            },
            component: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                props: { type: 'object' }
              }
            }
          }
        }
      }
    }
  }
};

// Esquema para configuración de tema
export const themeSchema = {
  type: 'object',
  properties: {
    name: { type: 'string', minLength: 1 },
    colors: {
      type: 'object',
      properties: {
        primary: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        secondary: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        background: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        surface: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        text: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        textSecondary: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        border: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        hover: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' },
        active: { type: 'string', pattern: '^#[0-9A-Fa-f]{6}$' }
      }
    },
    typography: {
      type: 'object',
      properties: {
        fontFamily: { type: 'string' },
        fontSize: {
          type: 'object',
          properties: {
            xs: { type: 'string' },
            sm: { type: 'string' },
            base: { type: 'string' },
            lg: { type: 'string' },
            xl: { type: 'string' }
          }
        },
        fontWeight: {
          type: 'object',
          properties: {
            normal: { type: 'number' },
            medium: { type: 'number' },
            semibold: { type: 'number' },
            bold: { type: 'number' }
          }
        },
        lineHeight: {
          type: 'object',
          properties: {
            tight: { type: 'number' },
            normal: { type: 'number' },
            relaxed: { type: 'number' }
          }
        }
      }
    },
    spacing: {
      type: 'object',
      properties: {
        xs: { type: 'string' },
        sm: { type: 'string' },
        md: { type: 'string' },
        lg: { type: 'string' },
        xl: { type: 'string' },
        xxl: { type: 'string' }
      }
    },
    borderRadius: {
      type: 'object',
      properties: {
        none: { type: 'string' },
        sm: { type: 'string' },
        md: { type: 'string' },
        lg: { type: 'string' },
        full: { type: 'string' }
      }
    },
    shadows: {
      type: 'object',
      properties: {
        none: { type: 'string' },
        sm: { type: 'string' },
        md: { type: 'string' },
        lg: { type: 'string' }
      }
    },
    transitions: {
      type: 'object',
      properties: {
        duration: { type: 'string' },
        easing: { type: 'string' }
      }
    }
  }
};

// Esquema principal de configuración
export const configSchema = {
  type: 'object',
  required: ['navigation', 'logo'],
  properties: {
    navigation: {
      type: 'array',
      items: navigationItemSchema,
      minItems: 1
    },
    logo: logoSchema,
    theme: themeSchema,
    settings: {
      type: 'object',
      properties: {
        defaultState: {
          type: 'string',
          enum: ['open', 'semiCollapsed', 'collapsed']
        },
        allowToggle: { type: 'boolean' },
        showTooltips: { type: 'boolean' },
        animationDuration: { type: 'number', minimum: 0 },
        responsive: {
          type: 'object',
          properties: {
            breakpoint: { type: 'number', minimum: 1 },
            mobileOverlay: { type: 'boolean' }
          }
        },
        accessibility: {
          type: 'object',
          properties: {
            keyboardNavigation: { type: 'boolean' },
            announceStateChanges: { type: 'boolean' },
            focusManagement: { type: 'boolean' }
          }
        }
      }
    },
    localization: {
      type: 'object',
      properties: {
        defaultLanguage: { type: 'string' },
        languages: {
          type: 'object',
          patternProperties: {
            '^[a-z]{2}(-[A-Z]{2})?$': {
              type: 'object',
              properties: {
                name: { type: 'string' },
                translations: { type: 'object' }
              }
            }
          }
        }
      }
    }
  },
  definitions: {
    navigationItem: navigationItemSchema,
    logoImage: {
      type: 'object',
      properties: {
        src: { type: 'string', minLength: 1 },
        alt: { type: 'string' },
        width: { type: 'number', minimum: 1 },
        height: { type: 'number', minimum: 1 }
      },
      required: ['src', 'alt']
    }
  }
};

/**
 * Valida un objeto de configuración contra el esquema
 * @param {Object} config - Configuración a validar
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export function validateConfig(config) {
  const errors = [];
  
  try {
    // Validación básica de estructura
    if (!config || typeof config !== 'object') {
      errors.push('La configuración debe ser un objeto válido');
      return { isValid: false, errors };
    }

    // Validar navegación
    if (!config.navigation || !Array.isArray(config.navigation)) {
      errors.push('La propiedad "navigation" es requerida y debe ser un array');
    } else if (config.navigation.length === 0) {
      errors.push('La navegación debe contener al menos un elemento');
    } else {
      config.navigation.forEach((item, index) => {
        const itemErrors = validateNavigationItem(item, `navigation[${index}]`);
        errors.push(...itemErrors);
      });
    }

    // Validar logo
    if (!config.logo || typeof config.logo !== 'object') {
      errors.push('La propiedad "logo" es requerida y debe ser un objeto');
    } else {
      const logoErrors = validateLogo(config.logo);
      errors.push(...logoErrors);
    }

    // Validar tema (opcional)
    if (config.theme) {
      const themeErrors = validateTheme(config.theme);
      errors.push(...themeErrors);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Error de validación: ${error.message}`]
    };
  }
}

function validateNavigationItem(item, path = '') {
  const errors = [];
  
  if (!item || typeof item !== 'object') {
    errors.push(`${path}: El elemento debe ser un objeto`);
    return errors;
  }

  if (!item.id || typeof item.id !== 'string') {
    errors.push(`${path}: La propiedad "id" es requerida y debe ser una cadena`);
  }

  if (!item.label || typeof item.label !== 'string') {
    errors.push(`${path}: La propiedad "label" es requerida y debe ser una cadena`);
  }

  if (!item.icon || typeof item.icon !== 'string') {
    errors.push(`${path}: La propiedad "icon" es requerida y debe ser una cadena`);
  }

  if (item.children && Array.isArray(item.children)) {
    item.children.forEach((child, index) => {
      const childErrors = validateNavigationItem(child, `${path}.children[${index}]`);
      errors.push(...childErrors);
    });
  }

  return errors;
}

function validateLogo(logo) {
  const errors = [];
  
  if (!logo.text || typeof logo.text !== 'string') {
    errors.push('logo.text es requerido y debe ser una cadena');
  }

  if (logo.image && typeof logo.image === 'object') {
    if (!logo.image.src || typeof logo.image.src !== 'string') {
      errors.push('logo.image.src es requerido cuando se especifica una imagen');
    }
    if (!logo.image.alt || typeof logo.image.alt !== 'string') {
      errors.push('logo.image.alt es requerido cuando se especifica una imagen');
    }
  }

  return errors;
}

function validateTheme(theme) {
  const errors = [];
  
  if (theme.colors && typeof theme.colors === 'object') {
    Object.entries(theme.colors).forEach(([key, value]) => {
      if (typeof value === 'string' && !/^#[0-9A-Fa-f]{6}$/.test(value)) {
        errors.push(`theme.colors.${key} debe ser un color hexadecimal válido (#RRGGBB)`);
      }
    });
  }

  return errors;
}